import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card, Table, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getMaterials } from '../../../services/MaterialLogsService';
import { getRecipes } from '../../../services/RecipesService';
import { getProducts } from '../../../services/ProductsService';
import { getOrders } from '../../../services/OrdersService';
import { getSalesDashboard } from '../../../services/SalesService';
import CountUp from 'react-countup';
import DeliveryCalendar from './DeliveryCalendar';
import ProjectOverviewTab from './elements/ProjectOverviewTab';
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    AreaChart, Area,
} from 'recharts';

// Delivery Status is the single source of truth for order fulfillment state (the legacy
// Order.status field has been retired — see OrderDetail.js / TasksPage.js).
const STATUS_COLORS = {
    preparing: '#6c757d',
    ready_to_deliver: '#0dcaf0',
    out_for_delivery: '#0d6efd',
    delivered: '#198754',
    cancelled: '#dc3545',
};

function formatDate(d) {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// Format a Date as YYYY-MM-DD in local time (so revenue matches the calendar day the user sees)
function toLocalDateKey(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

// Parse date string as local YYYY-MM-DD (avoid UTC midnight shifting to previous day)
function parseLocalDateKey(dateStr) {
    if (!dateStr) return null;
    const s = String(dateStr).trim().slice(0, 10);
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (match) {
        const y = parseInt(match[1], 10);
        const m = parseInt(match[2], 10) - 1;
        const d = parseInt(match[3], 10);
        const date = new Date(y, m, d);
        if (date.getFullYear() === y && date.getMonth() === m && date.getDate() === d) {
            return toLocalDateKey(date);
        }
    }
    const fallback = new Date(dateStr);
    if (!isNaN(fallback.getTime())) return toLocalDateKey(fallback);
    return null;
}

// Build continuous last-14-days revenue series from orders (all non-cancelled by order date)
function buildLast14DaysRevenue(orders) {
    const today = new Date();
    const dayKeys = [];
    const totals = {};

    for (let i = 13; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        const key = toLocalDateKey(d);
        dayKeys.push(key);
        totals[key] = 0;
    }

    for (const o of orders || []) {
        if ((o.delivery_status || 'preparing') === 'cancelled' || o.is_marketing) continue;   // giveaways are not revenue
        const dateStr = o.order_date || o.created_at;
        if (!dateStr) continue;
        const key = parseLocalDateKey(dateStr);
        if (!key || !(key in totals)) continue;
        totals[key] += parseFloat(o.total || 0);
    }

    return dayKeys.map((key) => ({
        date: key,
        revenue: Number(totals[key].toFixed(2)),
    }));
}

const Home = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [materials, setMaterials] = useState([]);
    const [recipes, setRecipes] = useState([]);
    const [products, setProducts] = useState([]);
    const [orders, setOrders] = useState([]);
    const [sales, setSales] = useState(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [mRes, rRes, pRes, oRes, sRes] = await Promise.all([
                    getMaterials().catch(() => ({ data: [] })),
                    getRecipes().catch(() => ({ data: [] })),
                    getProducts().catch(() => ({ data: [] })),
                    getOrders().catch(() => ({ data: [] })),
                    getSalesDashboard().catch(() => ({ data: null })),
                ]);
                setMaterials(mRes.data || []);
                setRecipes(rRes.data || []);
                setProducts(pRes.data || []);
                setOrders(oRes.data || []);
                setSales(sRes.data);
            } catch {
                setMaterials([]);
                setRecipes([]);
                setProducts([]);
                setOrders([]);
                setSales(null);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const revenueLast14Days = useMemo(
        () => buildLast14DaysRevenue(orders),
        [orders]
    );

    const orderStats = orders.reduce(
        (acc, o) => {
            const deliveryStatus = o.delivery_status || 'preparing';
            acc.byStatus[deliveryStatus] = (acc.byStatus[deliveryStatus] || 0) + 1;
            if (deliveryStatus === 'delivered' && !o.is_marketing) {
                acc.revenue += parseFloat(o.total || 0);
            }
            return acc;
        },
        { byStatus: {}, revenue: 0 }
    );

    const ordersByStatusChart = Object.entries(orderStats.byStatus).map(([status, count]) => ({
        name: t(`deliveryStatuses.${status}`, { defaultValue: status }),
        value: count,
        status,
        color: STATUS_COLORS[status] || '#6c757d',
    }));

    if (loading) {
        return (
            <div className="container-fluid">
                <div className="text-center py-5">
                    <Spinner animation="border" role="status" className="text-primary" />
                    <p className="mt-2 text-muted mb-0">Loading overview...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid">
            {/* Delivery Calendar + Overview Chart - half screen each */}
            <div className="row mb-4 g-3 dashboard-overview-pair">
                <div className="col-lg-6">
                    <DeliveryCalendar orders={orders} />
                </div>
                <div className="col-lg-6">
                    <ProjectOverviewTab height={340} orders={orders} materials={materials} recipes={recipes} products={products} sales={sales} />
                </div>
            </div>

            {/* Summary Cards - removed Recipes card per request */}

            {/* Sales Dashboard with Charts */}
            {sales && (
                <>
                    {/* Charts Row */}
                    <div className="row g-3 mb-4">
                        {/* Sales vs Cost vs Profit Bar */}
                        <div className="col-lg-4">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0">
                                    <h6 className="mb-0">Sales Overview</h6>
                                </Card.Header>
                                <Card.Body style={{ position: 'relative', paddingBottom: '10px' }}>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <BarChart
                                            data={sales.summary_bars || []}
                                            layout="vertical"
                                            margin={{ top: 6, right: 12, bottom: 0, left: 8 }}
                                        >
                                            <XAxis
                                                type="number"
                                                domain={[0, (dataMax) => Math.ceil((Number(dataMax) || 0) * 1.12)]}
                                                tickFormatter={(v) => `\u20AC${v}`}
                                            />
                                            <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 12 }} />
                                            <Tooltip formatter={(v) => [`\u20AC${Number(v).toFixed(2)}`, '']} />
                                            <Bar
                                                dataKey="value"
                                                name=""
                                                radius={[0, 4, 4, 0]}
                                                isAnimationActive={false}
                                                label={({ x, y, width, height, value }) => {
                                                    const text = `\u20AC${Number(value || 0).toFixed(2)}`;
                                                    const padding = 6;
                                                    const textX = width < 80 ? Number(x) + padding : Number(x) + Number(width) - 72;
                                                    return (
                                                        <text
                                                            x={textX}
                                                            y={Number(y) + Number(height) / 2}
                                                            dy={4}
                                                            textAnchor="start"
                                                            fill="#fff"
                                                            fontSize={12}
                                                            fontWeight={700}
                                                        >
                                                            {text}
                                                        </text>
                                                    );
                                                }}
                                            >
                                                {(sales.summary_bars || []).map((entry, i) => (
                                                    <Cell key={i} fill={entry.fill} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Orders by Status Pie */}
                        <div className="col-lg-4">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0 d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">Orders by Status</h6>
                                    <Link to="/orders" className="btn btn-sm btn-outline-primary">View all</Link>
                                </Card.Header>
                                <Card.Body>
                                    {ordersByStatusChart.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={220}>
                                            <PieChart>
                                                <Pie
                                                    data={ordersByStatusChart}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    cx="50%"
                                                    cy="50%"
                                                    innerRadius={50}
                                                    outerRadius={80}
                                                    paddingAngle={2}
                                                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                                >
                                                    {ordersByStatusChart.map((entry, i) => (
                                                        <Cell key={i} fill={entry.color || STATUS_COLORS[entry.status]} />
                                                    ))}
                                                </Pie>
                                                <Tooltip formatter={(v) => [v, 'Orders']} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <i className="fa-solid fa-chart-pie fa-3x mb-2 opacity-50" />
                                            <p className="mb-0">No orders yet</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>

                        {/* Revenue Over Time */}
                        <div className="col-lg-4">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0">
                                    <h6 className="mb-0">Revenue (Last 14 Days)</h6>
                                </Card.Header>
                                <Card.Body>
                                    {revenueLast14Days.length > 0 ? (
                                        <ResponsiveContainer width="100%" height={220}>
                                            <AreaChart data={revenueLast14Days}>
                                                <defs>
                                                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#0d6efd" stopOpacity={0.3} />
                                                        <stop offset="95%" stopColor="#0d6efd" stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                                                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5) || v} />
                                                <YAxis tickFormatter={(v) => `\u20AC${v}`} tick={{ fontSize: 10 }} />
                                                <Tooltip formatter={(v) => [`\u20AC${Number(v).toFixed(2)}`, 'Revenue']} labelFormatter={(l) => formatDate(l)} />
                                                <Area type="monotone" dataKey="revenue" stroke="#0d6efd" fillOpacity={1} fill="url(#colorRevenue)" />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <div className="text-center py-5 text-muted">
                                            <i className="fa-solid fa-chart-line fa-3x mb-2 opacity-50" />
                                            <p className="mb-0">No revenue data yet</p>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    {/* Top 10 Tables */}
                    <div className="row g-3 mb-4">
                        <div className="col-lg-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0">
                                    <h6 className="mb-0">{t('dashboard.top10Profit')}</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table className="mb-0 dashboard-top10-table" hover responsive>
                                        <thead className="bg-light">
                                            <tr>
                                                <th>{t('dashboard.colItem')}</th>
                                                <th className="text-end">{t('dashboard.colUnits')}</th>
                                                <th className="text-end">{t('dashboard.colRevenue')}</th>
                                                <th className="text-end">{t('dashboard.colProfit')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(!sales.top10_net_profit || sales.top10_net_profit.length === 0) ? (
                                                <tr><td colSpan="4" className="text-center text-muted py-4">{t('dashboard.noData')}</td></tr>
                                            ) : (
                                                sales.top10_net_profit.map((row, i) => (
                                                    <tr key={i}>
                                                        <td>{row.item}</td>
                                                        <td className="text-end">{row.units_sold}</td>
                                                        <td className="text-end">€{row.net_sales.toFixed(2)}</td>
                                                        <td className="text-end text-success fw-semibold">€{row.net_profit.toFixed(2)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-lg-6">
                            <Card className="border-0 shadow-sm h-100">
                                <Card.Header className="bg-white border-0">
                                    <h6 className="mb-0">{t('dashboard.top10Sold')}</h6>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table className="mb-0 dashboard-top10-table" hover responsive>
                                        <thead className="bg-light">
                                            <tr>
                                                <th>{t('dashboard.colItem')}</th>
                                                <th className="text-end">{t('dashboard.colUnits')}</th>
                                                <th className="text-end">{t('dashboard.colRevenue')}</th>
                                                <th className="text-end">{t('dashboard.colMargin')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(!sales.top10_most_sold || sales.top10_most_sold.length === 0) ? (
                                                <tr><td colSpan="4" className="text-center text-muted py-4">{t('dashboard.noData')}</td></tr>
                                            ) : (
                                                sales.top10_most_sold.map((row, i) => (
                                                    <tr key={i}>
                                                        <td>{row.item}</td>
                                                        <td className="text-end">{row.units_sold}</td>
                                                        <td className="text-end">€{row.net_sales.toFixed(2)}</td>
                                                        <td className="text-end">{row.profit_margin.toFixed(1)}%</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    {(sales.delivery_fees?.lines > 0 || sales.unlinked_items?.lines > 0) && (
                        <div className="mb-4 small">
                            {sales.delivery_fees?.lines > 0 && (
                                <div className="text-muted">
                                    {t('dashboard.deliveryFees', { count: sales.delivery_fees.lines, revenue: `€${sales.delivery_fees.net_sales.toFixed(2)}` })}
                                </div>
                            )}
                            {sales.unlinked_items?.lines > 0 && (
                                <div className="text-danger fw-semibold">
                                    {t('dashboard.unlinkedItems', {
                                        count: sales.unlinked_items.lines, units: sales.unlinked_items.units,
                                        gross: `€${sales.unlinked_items.gross_sales.toFixed(2)}`, orders: sales.unlinked_items.orders.join(', '),
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                </>
            )}
        </div>
    );
};

export default Home;

